import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import CountUp from "react-countup";
import { ArrowRight, ChevronDown, ShieldCheck } from "lucide-react";
import { IMAGES, HERO_STATS } from "../../data/siteData";

const NODES = [
  { top: "18%", left: "12%", size: 10, delay: "0s" },
  { top: "30%", left: "82%", size: 8, delay: "1.2s" },
  { top: "62%", left: "20%", size: 12, delay: "0.6s" },
  { top: "48%", left: "68%", size: 7, delay: "2s" },
  { top: "74%", left: "88%", size: 9, delay: "1.6s" },
  { top: "22%", left: "55%", size: 6, delay: "0.9s" },
];

const LINES = [
  "M120,160 L640,300",
  "M640,300 L320,520",
  "M320,520 L900,420",
  "M900,420 L560,200",
  "M560,200 L120,160",
];

const scrollTo = (e, href) => {
  e.preventDefault();
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Hero = ({ content }) => {
  const hero = content?.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const title = "Telecommunications Infrastructure Built For The Future";
  const description =
    "A specialist, hands-on telecommunications contractor available for projects anywhere in Australia — tower rigging, wireless, fibre and maintenance, mobilised rapidly with an uncompromising focus on safety, quality and reliability.";

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#050F22]"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY, backgroundImage: `url('${IMAGES.heroBackground}')` }}
        className="absolute inset-0 -top-24 bg-cover bg-center"
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050F22] via-[#081C3A]/85 to-[#081C3A]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050F22] via-transparent to-[#050F22]/60" />
      <div className="absolute inset-0 hero-grid-overlay opacity-70" />

      {/* Animated network lines + nodes */}
      <svg className="absolute inset-0 w-full h-full opacity-50" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid slice">
        {LINES.map((d, i) => (
          <path key={i} d={d} fill="none" stroke="#00D4FF" strokeWidth="1.2" className="dash-line" style={{ opacity: 0.5 }} />
        ))}
      </svg>
      {NODES.map((n, i) => (
        <div key={i} className="node animate-float-node" style={{ top: n.top, left: n.left, animationDelay: n.delay }}>
          <span
            className="block w-full h-full rounded-full animate-glow-pulse"
            style={{ width: n.size, height: n.size, background: "#00D4FF", boxShadow: "0 0 14px 3px rgba(0,212,255,0.9)" }}
          />
        </div>
      ))}

      {/* Content */}
      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative container-custom pt-32 pb-40 lg:py-0 z-10">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="eyebrow mb-6"
          >
            Licensed Telecommunications Infrastructure Contractor
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.05] mt-5 mb-7"
          >
            Telecommunications Infrastructure{" "}
            <span className="text-[#00D4FF] text-glow">Built For The Future</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-lg lg:text-xl text-slate-300 max-w-2xl mb-10 leading-relaxed"
          >
            {description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a href="#contact" onClick={(e) => scrollTo(e, "#contact")} className="btn-cyan" data-testid="hero-quote-btn">
              Request a Quote <ArrowRight className="w-5 h-5" />
            </a>
            <a href="#experience" onClick={(e) => scrollTo(e, "#experience")} className="btn-ghost" data-testid="hero-capabilities-btn">
              Our Capabilities
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-9"
            data-testid="hero-credentials"
          >
            {["Licensed & Insured", "Safety Accredited", "Australia-Wide Delivery"].map((c) => (
              <span key={c} className="inline-flex items-center gap-2 text-sm text-slate-300">
                <ShieldCheck className="w-4 h-4 text-[#00D4FF]" />
                {c}
              </span>
            ))}
          </motion.div>
        </div>
      </motion.div>

      {/* Stats bar */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-white/10 bg-[#050F22]/70 backdrop-blur-md" data-testid="stats-bar">
        <div className="container-custom py-7">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {HERO_STATS.map((stat, i) => (
              <div key={i} className="text-center lg:text-left" data-testid={`hero-stat-${i}`}>
                <div className="text-3xl lg:text-4xl font-extrabold text-[#00D4FF]">
                  {stat.display ? (
                    stat.display
                  ) : (
                    <CountUp end={stat.value} suffix={stat.suffix} duration={2.2} enableScrollSpy scrollSpyOnce />
                  )}
                </div>
                <div className="text-xs lg:text-sm text-slate-400 mt-1 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <a
        href="#services"
        onClick={(e) => scrollTo(e, "#services")}
        className="hidden lg:flex absolute bottom-28 left-1/2 -translate-x-1/2 text-slate-400 hover:text-[#00D4FF] flex-col items-center gap-1 animate-bounce"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-6 h-6" />
      </a>
    </section>
  );
};
